import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDjAuthnDomain, getDjAuthzDomain, getDjClientId } from '../utils/config';
import { clearStoredNonce, generateNonce, storeNonce } from '../utils/auth';
import { getItem, removeItem, setItem, STORAGE_KEYS } from '../utils/local-storage';
import { AuthError } from '../utils/auth-error';

const DJ_CALLBACK_PATH = '/login/callback/dow-jones';
const DJ_LOGIN_REDIRECT_PATH = '/login-dowjones-redirect';
const DJ_PRODUCT_NAME = 'fss';

const DJ_APPROVED_PARAMS = new Set(['accountid', 'namespace', 'productid', 'emglsauthnidpid', 'xsid']);

export type DjLoginSuccessResult = 'success' | 'redirecting';

type DjTokens = {
  id_token: string;
  access_token: string;
  refresh_token?: string;
};

export type DjAuth = {
  login: () => void;
  loginSuccess: (tokens: DjTokens) => Promise<DjLoginSuccessResult>;
  logout: () => void;
  refreshTokens: () => Promise<void>;
  isAuthenticated: () => boolean;
};

type DjAuthState = {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
};

type DjAuthContextValue = {
  djAuth: DjAuth;
  djAuthState: DjAuthState | null;
};

type AuthzTokenResponse = {
  access_token: string;
  [key: string]: unknown;
};

type AuthzTokenResult =
  | {
      status: 'success';
      data: AuthzTokenResponse;
    }
  | {
      status: 'redirecting';
    };

const DjAuthContext = createContext<DjAuthContextValue | undefined>(undefined);

function isDjApprovedParam(key: string): boolean {
  return DJ_APPROVED_PARAMS.has(key.toLowerCase());
}

function isSafeDjReturnUrl(url: URL): boolean {
  return !url.pathname.startsWith('/login/callback') && url.pathname !== DJ_LOGIN_REDIRECT_PATH;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const payload = token.split('.')[1];

  if (!payload) {
    throw new Error('Invalid JWT: missing payload');
  }

  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

  return JSON.parse(window.atob(paddedBase64));
}

async function readJsonSafely<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

export function buildDjAuthUrl(params: {
  location: Location;
  clientId?: string;
  authnDomain?: string;
  nonce: string;
  device: string;
}): string {
  const { location, clientId, authnDomain, nonce, device } = params;

  const scope = encodeURIComponent('openid profile email pib offline_access');
  const responseType = encodeURIComponent('id_token token');
  const redirectUri = encodeURIComponent(`${location.origin}${DJ_CALLBACK_PATH}`);

  const urlParams = new URLSearchParams(location.search);
  const uri = urlParams.get('uri');

  let state = '';

  // Valid Dow Jones params may be provided directly on /login-dowjones-redirect.
  // Unknown query params are intentionally ignored.
  const djQueryParams: [string, string][] = [];
  for (const [key, value] of urlParams.entries()) {
    if (isDjApprovedParam(key)) {
      djQueryParams.push([key, value]);
    }
  }

  if (uri) {
    try {
      const uriUrl = new URL(uri, location.origin);

      if (isSafeDjReturnUrl(uriUrl)) {
        // ignore any query params in the return URI
        state = `${uriUrl.pathname}`;
      } else {
        console.warn('Ignoring unsafe Dow Jones return uri:', uri);
      }
    } catch (error) {
      console.warn('Ignoring invalid Dow Jones return uri:', error);
    }
  }

  const authUrl =
    `https://${authnDomain}/oauth2/v1/authorize` +
    `?client_id=${encodeURIComponent(clientId || '')}` +
    `&scope=${scope}` +
    `&response_type=${responseType}` +
    `&redirect_uri=${redirectUri}` +
    `&productName=${encodeURIComponent(DJ_PRODUCT_NAME)}` +
    `&nonce=${encodeURIComponent(nonce)}` +
    `&device=${encodeURIComponent(device)}` +
    (state ? `&state=${encodeURIComponent(state)}` : '') +
    djQueryParams.map(([key, value]) => `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('');

  return authUrl;
}

export const DjAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [djAuthState, setAuthState] = useState<DjAuthState | null>(null);
  const navigate = useNavigate();

  const isTokenExpired = (token: string): boolean => {
    try {
      const payload = decodeJwtPayload(token);
      const expiresAt = typeof payload.exp === 'number' ? payload.exp : 0;
      const currentTime = Math.floor(Date.now() / 1000);

      return expiresAt <= currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  };

  const clearAuthState = () => {
    removeItem(STORAGE_KEYS.DJ_ACCESS_TOKEN);
    removeItem(STORAGE_KEYS.DJ_AUTHN_ACCESS_TOKEN);
    removeItem(STORAGE_KEYS.DJ_REFRESH_TOKEN);
    removeItem(STORAGE_KEYS.DJ_NONCE);

    setAuthState({
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
    });
  };

  const login = () => {
    const clientId = getDjClientId();
    const nonce = generateNonce();
    const randomString = Math.random().toString(36).substring(2, 6);
    const device = `browser-${Date.now()}-${randomString}`;
    const authnDomain = getDjAuthnDomain();

    const authUrl = buildDjAuthUrl({
      location: window.location,
      clientId,
      authnDomain,
      nonce,
      device,
    });

    clearStoredNonce();
    storeNonce(nonce);

    window.location.href = authUrl;
  };

  const submitActionInfoForm = (actionUrl: string, actionPayload: Record<string, unknown>) => {
    const returnUri = `${window.location.origin}${DJ_CALLBACK_PATH}`;
    const formData = new URLSearchParams();

    for (const [key, value] of Object.entries(actionPayload || {})) {
      if (value !== undefined && value !== null) {
        formData.append(key, String(value));
      }
    }

    formData.set('return_uri', returnUri);
    formData.set('response_mode', 'fragment');

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = actionUrl;

    for (const [key, value] of formData.entries()) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    }

    document.body.appendChild(form);
    form.submit();
  };

  const getAuthzToken = async (tokens: DjTokens): Promise<AuthzTokenResult> => {
    try {
      const response = await fetch(`https://${getDjAuthnDomain()}/oauth2/v1/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: getDjClientId() || '',
          grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
          scope: 'openid pib',
          assertion: tokens.id_token,
          access_token: tokens.access_token,
        }),
      });

      const responseBody = await readJsonSafely<{
        access_token?: string;
        error_description?: string;
        error?: string;
        action_info?: {
          action_url?: string;
          action_payload?: Record<string, unknown>;
        };
      }>(response);

      if (!response.ok) {
        if (responseBody?.action_info?.action_url) {
          submitActionInfoForm(responseBody.action_info.action_url, responseBody.action_info.action_payload || {});
          return { status: 'redirecting' };
        }

        throw new AuthError(
          'Failed to fetch AuthZ token: ' + (responseBody?.error_description || responseBody?.error || response.statusText)
        );
      }

      if (!responseBody?.access_token) {
        throw new AuthError('Failed to fetch AuthZ token: missing access token in response');
      }

      const accessToken = responseBody.access_token;

      return {
        status: 'success',
        data: {
          ...responseBody,
          access_token: accessToken,
        },
      };
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('Failed to obtain AuthZ token: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const isAuthorized = async (accessToken: string) => {
    try {
      const response = await fetch(`https://${getDjAuthzDomain()}/users/entitlements?parts=FSS`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const responseData = await readJsonSafely<{
        message?: string;
        text?: string;
        data?: {
          attributes?: {
            factiva_sentiment_signals?: {
              is_on?: boolean;
            };
          };
        };
      }>(response);

      if (!response.ok) {
        throw new AuthError('Authorization check failed: ' + (responseData?.message || responseData?.text || response.statusText));
      }

      if (!responseData?.data?.attributes?.factiva_sentiment_signals?.is_on) {
        throw new AuthError(
          'User not authorized: ' + (responseData?.text || 'missing FSS entitlement'),
          'You are not authorized to access this service.'
        );
      }

      return true;
    } catch (error) {
      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('Authorization error: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const loginSuccess = async (tokens: DjTokens): Promise<DjLoginSuccessResult> => {
    setItem(STORAGE_KEYS.DJ_AUTHN_ACCESS_TOKEN, tokens.access_token);

    const authzResult = await getAuthzToken(tokens);

    if (authzResult.status === 'redirecting') {
      return 'redirecting';
    }

    const authzAccessToken = authzResult.data.access_token;

    setItem(STORAGE_KEYS.DJ_ACCESS_TOKEN, authzAccessToken);

    if (tokens.refresh_token) {
      setItem(STORAGE_KEYS.DJ_REFRESH_TOKEN, tokens.refresh_token);
    }

    clearStoredNonce();

    await isAuthorized(authzAccessToken);

    setAuthState({
      isAuthenticated: true,
      accessToken: authzAccessToken,
      refreshToken: tokens.refresh_token || null,
    });

    return 'success';
  };

  const logout = async () => {
    const accessToken = getItem(STORAGE_KEYS.DJ_ACCESS_TOKEN);

    if (accessToken) {
      const redirectUri = encodeURIComponent(`${window.location.origin}/login`);
      const logoutUrl =
        `https://${getDjAuthnDomain()}/oauth2/v1/logout` +
        `?access_token=${encodeURIComponent(accessToken)}` +
        `&return_uri=${redirectUri}` +
        `&productName=${encodeURIComponent(DJ_PRODUCT_NAME)}`;

      clearAuthState();
      window.location.href = logoutUrl;
      return;
    }

    clearAuthState();
    navigate('/login');
  };

  const refreshTokens = async () => {
    const refreshToken = getItem(STORAGE_KEYS.DJ_REFRESH_TOKEN);
    const authnAccessToken = getItem(STORAGE_KEYS.DJ_AUTHN_ACCESS_TOKEN);

    if (!refreshToken) {
      void logout();
      return;
    }

    try {
      const response = await fetch(`https://${getDjAuthnDomain()}/oauth2/v1/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: getDjClientId() || '',
          refresh_token: refreshToken,
          scope: 'openid pib',
        }),
      });

      const responseBody = await readJsonSafely<{
        access_token?: string;
        error_description?: string;
        error?: string;
      }>(response);

      if (!response.ok) {
        throw new AuthError('Failed to refresh tokens: ' + (responseBody?.error_description || responseBody?.error || response.statusText));
      }

      const authnIdToken = responseBody?.access_token;

      if (!authnIdToken) {
        throw new AuthError('Failed to refresh tokens: missing AuthN access token in response');
      }

      const authzResponse = await fetch(`https://${getDjAuthnDomain()}/oauth2/v1/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: getDjClientId() || '',
          grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
          scope: 'openid pib',
          assertion: authnIdToken,
          access_token: authnAccessToken || '',
        }),
      });

      const authzBody = await readJsonSafely<{
        access_token?: string;
        error_description?: string;
        error?: string;
      }>(authzResponse);

      if (!authzResponse.ok) {
        throw new AuthError(
          'Failed to refresh AuthZ access token: ' + (authzBody?.error_description || authzBody?.error || authzResponse.statusText)
        );
      }

      const authzToken = authzBody?.access_token;

      if (!authzToken) {
        throw new AuthError('Failed to refresh AuthZ access token: missing access token in response');
      }

      setItem(STORAGE_KEYS.DJ_ACCESS_TOKEN, authzToken);

      setAuthState({
        isAuthenticated: true,
        accessToken: authzToken,
        refreshToken,
      });
    } catch (error) {
      console.error('Error refreshing tokens:', error);
      void logout();
    }
  };

  const isAuthenticated = (): boolean => {
    const accessToken = getItem(STORAGE_KEYS.DJ_ACCESS_TOKEN);
    return !!accessToken && !isTokenExpired(accessToken);
  };

  useEffect(() => {
    const accessToken = getItem(STORAGE_KEYS.DJ_ACCESS_TOKEN);
    const refreshToken = getItem(STORAGE_KEYS.DJ_REFRESH_TOKEN);

    if (accessToken && !isTokenExpired(accessToken)) {
      setAuthState({
        isAuthenticated: true,
        accessToken,
        refreshToken,
      });
    } else if (refreshToken) {
      void refreshTokens();
    } else {
      setAuthState({
        isAuthenticated: false,
        accessToken: null,
        refreshToken: null,
      });
    }
    // Run once on mount. refreshTokens is intentionally not included because it is recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const scheduleTokenRefresh = () => {
      const accessToken = getItem(STORAGE_KEYS.DJ_ACCESS_TOKEN);

      if (!accessToken) {
        return undefined;
      }

      try {
        const payload = decodeJwtPayload(accessToken);
        const expiresAt = typeof payload.exp === 'number' ? payload.exp : 0;
        const expirationTimeMilliseconds = expiresAt * 1000;
        const currentTime = Date.now();
        const timeUntilExpiration = expirationTimeMilliseconds - currentTime - 60000;

        if (timeUntilExpiration > 0) {
          // Schedule refresh 1 minute before expiration.
          return window.setTimeout(() => void refreshTokens(), timeUntilExpiration);
        }

        void refreshTokens();
        return undefined;
      } catch (error) {
        console.error('Error scheduling token refresh:', error);
        void refreshTokens();
        return undefined;
      }
    };

    const timeoutId = scheduleTokenRefresh();

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [djAuthState?.accessToken]);

  const djAuth: DjAuth = {
    login,
    logout,
    refreshTokens,
    isAuthenticated,
    loginSuccess,
  };

  return <DjAuthContext.Provider value={{ djAuth, djAuthState }}>{children}</DjAuthContext.Provider>;
};

export function useDjAuth(): DjAuthContextValue {
  const context = useContext(DjAuthContext);

  if (!context) {
    throw new Error('useDjAuth must be used within a DjAuthProvider');
  }

  return context;
}
